import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Save, Plus, X, Trash2 } from "lucide-react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { US_STATES } from "@/lib/constants";
import { z } from "zod";
import { UniversityEditableCard } from "./UniversityEditableCard";

interface Region {
  id: string;
  name: string;
  universities?: number;
  total_members?: number;
  active_prayer_cells?: number;
  states?: string[];
  bible_studies?: string[];
  prayer_cells?: string[];
  description?: string;
  coordinator_name?: string;
  coordinator_email?: string;
  coordinator_phone?: string;
}

interface University {
  id?: string;
  name: string;
  city: string;
  state: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
}

interface StateStats {
  state: string;
  total_members: number;
  active_prayer_cells: number;
}

interface RegionSettingsTabProps {
  regionId: string;
  isGlobalAdmin: boolean;
}

const universitySchema = z.object({
  name: z.string().trim().min(1, "University name is required").max(150, "Name must be less than 150 characters"),
  city: z.string().trim().min(1, "City is required").max(100, "City must be less than 100 characters"),
  state: z.string().refine((val) => US_STATES.includes(val), "Please select a valid state"),
  contact_person: z.string().trim().max(100, "Name must be less than 100 characters").optional(),
  contact_email: z.string().trim().email("Invalid email").max(150).optional().or(z.literal('')),
  contact_phone: z.string().trim().max(20).optional(),
});

export const RegionSettingsTab: React.FC<RegionSettingsTabProps> = ({ regionId, isGlobalAdmin }) => {
  const [region, setRegion] = useState<Region | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    universities: 0,
    total_members: 0,
    active_prayer_cells: 0,
    states: [] as string[],
    bible_studies: [] as string[],
    prayer_cells: [] as string[],
    description: '',
    coordinator_name: '',
    coordinator_email: '',
    coordinator_phone: '',
    stateStats: [] as StateStats[]
  });
  const [universitiesDraft, setUniversitiesDraft] = useState<University[]>([]);
  const [newUniversity, setNewUniversity] = useState({ 
    name: '', 
    city: '', 
    state: '',
    contact_person: '',
    contact_email: '',
    contact_phone: ''
  });
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchRegion();
  }, [regionId]);

  const fetchRegion = async () => {
    try {
      const [regionResult, coordinatorResult, universitiesResult] = await Promise.all([
        supabase.from('regions').select('*').eq('id', regionId).single(),
        supabase.from('members')
          .select('full_name, email, phone, role')
          .eq('region_id', regionId)
          .in('role', ['regional_president', 'regional_vice_president', 'regional_secretary', 'regional_joint_secretary', 'regional_treasurer'])
          .eq('is_active', true)
          .is('end_year', null)
          .order('start_year', { ascending: false }),
        supabase.from('universities').select('id, name, city, state').eq('region_id', regionId)
      ]);

      if (regionResult.error) throw regionResult.error;

      const data = regionResult.data;
      
      // Find best available coordinator by priority
      const rolePriority = ['regional_president', 'regional_vice_president', 'regional_secretary', 'regional_joint_secretary', 'regional_treasurer'];
      const coordinator = rolePriority
        .map(role => coordinatorResult.data?.find((c: any) => c.role === role))
        .find(c => c);

      setRegion(data);
      setFormData({
        universities: data.universities || 0,
        total_members: data.total_members || 0,
        active_prayer_cells: data.active_prayer_cells || 0,
        states: data.states || [],
        bible_studies: data.bible_studies || [],
        prayer_cells: data.prayer_cells || [],
        description: data.description || '',
        coordinator_name: coordinator?.full_name || data.coordinator_name || '',
        coordinator_email: coordinator?.email || data.coordinator_email || '',
        coordinator_phone: coordinator?.phone || data.coordinator_phone || '',
        stateStats: data.state_stats || []
      });
      
      setUniversitiesDraft(universitiesResult.data || []);
    } catch (error) {
      console.error('Error fetching region:', error);
      toast({
        title: "Error",
        description: "Failed to load region data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Derive aggregate region stats from per-state inputs so public pages
      // (Conference Hub cards, region badges) reflect what the regional admin
      // actually entered. Falls back to the existing flat value if no state
      // stats were provided.
      const aggMembers = formData.stateStats.reduce((sum, s) => sum + (s.total_members || 0), 0);
      const aggPrayerCells = formData.stateStats.reduce((sum, s) => sum + (s.active_prayer_cells || 0), 0);
      const universitiesCount = universitiesDraft.length;

      // Update regions table
      const { error: regionError } = await supabase
        .from('regions')
        .update({
          universities: universitiesCount,
          total_members: aggMembers > 0 ? aggMembers : formData.total_members,
          active_prayer_cells: aggPrayerCells > 0 ? aggPrayerCells : formData.active_prayer_cells,
          states: formData.states,
          bible_studies: formData.bible_studies,
          prayer_cells: formData.prayer_cells,
          description: formData.description,
          coordinator_name: formData.coordinator_name,
          coordinator_email: formData.coordinator_email,
          coordinator_phone: formData.coordinator_phone,
          state_stats: formData.stateStats
        })
        .eq('id', regionId);

      if (regionError) throw regionError;

      // Handle universities: fetch existing IDs
      const { data: existingUniversitiesInDB } = await supabase
        .from('universities')
        .select('id')
        .eq('region_id', regionId);

      const existingIds = (existingUniversitiesInDB || []).map(u => u.id);
      const draftIds = universitiesDraft.filter(u => u.id).map(u => u.id!);
      
      // Delete removed universities
      const deletedIds = existingIds.filter(id => !draftIds.includes(id));
      if (deletedIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('universities')
          .delete()
          .in('id', deletedIds);
        if (deleteError) throw deleteError;
      }

      // Separate new and existing universities from draft
      const universitiesToUpdate = universitiesDraft.filter(u => u.id);
      const universitiesToInsert = universitiesDraft.filter(u => !u.id);

      // Update existing universities
      if (universitiesToUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('universities')
          .upsert(universitiesToUpdate.map(u => ({
            id: u.id,
            region_id: regionId,
            name: u.name,
            city: u.city,
            state: u.state,
            contact_person: u.contact_person || null,
            contact_email: u.contact_email || null,
            contact_phone: u.contact_phone || null,
          })), { onConflict: 'id' });
        if (updateError) throw updateError;
      }

      // Insert new universities with has_fellowship set to true
      if (universitiesToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('universities')
          .insert(universitiesToInsert.map(u => ({
            region_id: regionId,
            name: u.name,
            city: u.city,
            state: u.state,
            has_fellowship: true,
            contact_person: u.contact_person || null,
            contact_email: u.contact_email || null,
            contact_phone: u.contact_phone || null,
          })));
        if (insertError) throw insertError;
      }

      try {
        const mod = await import("@/lib/conferencePrefetch");
        mod.invalidateConferenceHubCache?.();
      } catch {}

      toast({
        title: "Success",
        description: "Region settings updated — public Conference page will reflect changes on next load."
      });

      fetchRegion();
    } catch (error) {
      console.error('Error updating region:', error);
      toast({
        title: "Error",
        description: "Failed to update region settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const addUniversity = () => {
    try {
      universitySchema.parse(newUniversity);
      setUniversitiesDraft([...universitiesDraft, { ...newUniversity }]);
      setNewUniversity({ 
        name: '', 
        city: '', 
        state: '',
        contact_person: '',
        contact_email: '',
        contact_phone: ''
      });
      setValidationErrors({});
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: { [key: string]: string } = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            errors[error.path[0].toString()] = error.message;
          }
        });
        setValidationErrors(errors);
      }
    }
  };

  const removeUniversity = (index: number) => {
    setUniversitiesDraft(universitiesDraft.filter((_, i) => i !== index));
  };

  const updateUniversity = (index: number, updatedUni: University) => {
    const updated = [...universitiesDraft];
    updated[index] = updatedUni;
    setUniversitiesDraft(updated);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading region settings...</div>;
  }

  const aggregateTotalMembers = formData.stateStats.reduce((sum, s) => sum + (s.total_members || 0), 0);
  const aggregatePrayerCells = formData.stateStats.reduce((sum, s) => sum + (s.active_prayer_cells || 0), 0);

  const handleStateStatsChange = (state: string, field: 'total_members' | 'active_prayer_cells', value: number) => {
    const updated = [...formData.stateStats];
    const index = updated.findIndex(s => s.state === state);
    if (index >= 0) {
      updated[index] = { ...updated[index], [field]: value };
    } else {
      updated.push({ state, total_members: 0, active_prayer_cells: 0, [field]: value });
    }
    setFormData({ ...formData, stateStats: updated });
  };

  const getStateStats = (state: string) => {
    return formData.stateStats.find(s => s.state === state) || { state, total_members: 0, active_prayer_cells: 0 };
  };

  return (
    <div className="space-y-6">
      {/* Aggregate Summary Card */}
      <Card className="border-primary/50 bg-primary/5">
        <CardHeader>
          <CardTitle>Region Statistics Summary</CardTitle>
          <CardDescription>Automatically calculated from state-level data below</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-1">Total Universities</p>
              <p className="text-3xl font-bold">{universitiesDraft.length}</p>
            </div>
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-1">Total Members</p>
              <p className="text-3xl font-bold">{aggregateTotalMembers}</p>
            </div>
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-1">Active Prayer Cells</p>
              <p className="text-3xl font-bold">{aggregatePrayerCells}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>States</CardTitle>
          <CardDescription>Manage states covered by this region</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MultiSelectDropdown
            items={US_STATES}
            selected={formData.states}
            onChange={(states) => setFormData({ ...formData, states })}
            placeholder="Select states..."
            searchPlaceholder="Search states..."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>State-Level Statistics</CardTitle>
          <CardDescription>Add detailed statistics for each active state in your region</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 mb-4">
            <Label>Active States</Label>
            <MultiSelectDropdown
              items={formData.states.length > 0 ? formData.states : US_STATES}
              selected={formData.prayer_cells}
              onChange={(cells) => setFormData({ ...formData, prayer_cells: cells })}
              placeholder="Select active states..."
              searchPlaceholder="Search states..."
            />
            <p className="text-xs text-muted-foreground">
              These states will show OFUESI markers on the map. Only states selected in the States field above are available.
            </p>
          </div>

          {/* State Stats Table */}
          {formData.prayer_cells.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3 font-medium">State</th>
                    <th className="text-left p-3 font-medium">Total Members</th>
                    <th className="text-left p-3 font-medium">Active Prayer Cells</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.prayer_cells.map((state, index) => {
                    const stats = getStateStats(state);
                    return (
                      <tr key={state} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                        <td className="p-3 font-medium">{state}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            min="0"
                            value={stats.total_members || 0}
                            onChange={(e) => handleStateStatsChange(state, 'total_members', parseInt(e.target.value) || 0)}
                            className="w-32"
                          />
                        </td>
                        <td className="p-3">
                          <Input
                            type="number"
                            min="0"
                            value={stats.active_prayer_cells || 0}
                            onChange={(e) => handleStateStatsChange(state, 'active_prayer_cells', parseInt(e.target.value) || 0)}
                            className="w-32"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Universities</CardTitle>
          <CardDescription>Universities where you are actively working</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add University Form */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-university-name">University Name *</Label>
                <Input
                  id="new-university-name"
                  value={newUniversity.name}
                  onChange={(e) => setNewUniversity({ ...newUniversity, name: e.target.value })}
                  placeholder="e.g., Stanford University"
                  className={validationErrors.name ? "border-destructive" : ""}
                />
                {validationErrors.name && (
                  <p className="text-xs text-destructive">{validationErrors.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-university-city">City *</Label>
                <Input
                  id="new-university-city"
                  value={newUniversity.city}
                  onChange={(e) => setNewUniversity({ ...newUniversity, city: e.target.value })}
                  placeholder="e.g., Stanford"
                  className={validationErrors.city ? "border-destructive" : ""}
                />
                {validationErrors.city && (
                  <p className="text-xs text-destructive">{validationErrors.city}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-university-state">State *</Label>
                <MultiSelectDropdown
                  items={formData.states.length > 0 ? formData.states : US_STATES}
                  selected={newUniversity.state ? [newUniversity.state] : []}
                  onChange={(states) => setNewUniversity({ ...newUniversity, state: states[0] || '' })}
                  placeholder="Select state..."
                  searchPlaceholder="Search states..."
                />
                {validationErrors.state && (
                  <p className="text-xs text-destructive">{validationErrors.state}</p>
                )}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">University Coordinator (Optional)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-coordinator-name">Coordinator Name</Label>
                  <Input
                    id="new-coordinator-name"
                    value={newUniversity.contact_person}
                    onChange={(e) => setNewUniversity({ ...newUniversity, contact_person: e.target.value })}
                    placeholder="e.g., John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-coordinator-email">Email</Label>
                  <Input
                    id="new-coordinator-email"
                    type="email"
                    value={newUniversity.contact_email}
                    onChange={(e) => setNewUniversity({ ...newUniversity, contact_email: e.target.value })}
                    placeholder="coordinator@university.edu"
                    className={validationErrors.contact_email ? "border-destructive" : ""}
                  />
                  {validationErrors.contact_email && (
                    <p className="text-xs text-destructive">{validationErrors.contact_email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-coordinator-phone">Phone</Label>
                  <Input
                    id="new-coordinator-phone"
                    type="tel"
                    value={newUniversity.contact_phone}
                    onChange={(e) => setNewUniversity({ ...newUniversity, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button type="button" onClick={addUniversity}>
                <Plus className="h-4 w-4 mr-2" />
                Add University
              </Button>
            </div>
          </div>

          {/* Universities List */}
          {universitiesDraft.length > 0 && (
            <div className="space-y-2">
              <Label>Current Universities ({universitiesDraft.length})</Label>
              <div className="space-y-2">
                {universitiesDraft.map((uni, index) => (
                  <UniversityEditableCard
                    key={uni.id || index}
                    university={uni}
                    index={index}
                    states={formData.states}
                    onUpdate={updateUniversity}
                    onRemove={removeUniversity}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Region Information</CardTitle>
          <CardDescription>General information about the region</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter region description"
            />
          </div>

          <div className="bg-muted/50 p-4 rounded-lg mb-4">
            <p className="text-sm text-muted-foreground">
              ℹ️ Regional coordinator information is automatically synced from the Members section. To update, edit the regional president in the Members tab.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="coordinator_name">Coordinator Name</Label>
              <Input
                id="coordinator_name"
                value={formData.coordinator_name}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="coordinator_email">Coordinator Email</Label>
              <Input
                id="coordinator_email"
                type="email"
                value={formData.coordinator_email}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="coordinator_phone">Coordinator Phone</Label>
              <Input
                id="coordinator_phone"
                type="tel"
                value={formData.coordinator_phone}
                disabled
                className="bg-muted"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};
